import { Link } from "react-router-dom";
import { ToolLink } from "@/lib/types/app";
import React from "react";
import { BookmarkSimple, Lock } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip";
import PilotBadge from "@/components/PilotBadge";

interface ToolCardProps {
  tool: ToolLink;
  bookmarks: string[];
  toggleBookmark: (id: string) => void;
  userIsPilot?: boolean;
}

export function ToolCard({
  tool,
  bookmarks,
  toggleBookmark,
  userIsPilot = false,
}: ToolCardProps) {
  const { icon } = tool;
  const handleBookmark = (e: React.MouseEvent) => {
    if (!tool.active) {
      e.preventDefault();
    } else {
      e.preventDefault();
      toggleBookmark(tool.id);
    }
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="relative">
          {tool.isPilot && userIsPilot && (
            <div className="absolute z-20 -top-2 -left-4 -rotate-6">
              <PilotBadge variant="small" />
            </div>
          )}
          {tool.isNew && !tool.comingSoon && (
            <div className="absolute z-20 -top-2 -right-6 rotate-12">
              <div className="relative inline-flex items-center border-none gap-1 px-2 py-1 text-xs font-bold rounded-lg shadow-[3px_3px_0px_0px_rgba(0,0,0,0.4)] font-mono overflow-hidden bg-sidebar-ring text-sidebar-border">
                <span className="relative z-10">NEW!</span>
              </div>
            </div>
          )}
          {tool.comingSoon ? (
            <div
              className={cn(
                "block transition-shadow border border-light-grey bg-light rounded-2xl overflow-hidden h-full"
              )}
            >
              <div className="relative float-right w-12 h-12 ">
                <Lock
                  className="mt-4 rounded-tl-none rounded-br-none rounded-tr-md"
                  weight="bold"
                  size="24"
                />
              </div>
              <div className="px-5 py-5">
                <div className="mb-16 rounded-2xl bg-secondary aspect-square max-w-14 max-h-14">
                  {/* @ts-expect-error bad typing */}
                  {React.createElement(icon, {
                    className: "w-full h-full p-3",
                  })}
                </div>
                <div className="self-end">
                  <h1 className="text-card-content">{tool.title}</h1>
                  <p className="text-small-content text-card-foreground text-[0.938rem] mt-[0.625rem]">
                    {tool.description}
                  </p>
                  <div className="absolute top-0 left-0 flex items-center justify-center w-full h-full bg-black/30 rounded-2xl">
                    <span className="text-lg font-bold text-card-foreground">
                      Coming Soon!
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <Link
              to={tool.href}
              className={cn(
                "block transition-shadow border border-light-grey bg-light rounded-2xl hover:shadow-md overflow-hidden h-full",
                !tool.active && "cursor-pointer",
                tool.active && "cursor-pointer"
              )}
            >
              {tool.active ? (
                <Button
                  asChild
                  variant="ghost"
                  size="icon"
                  onClick={handleBookmark}
                  className="relative float-right w-12 h-12 transition-shadow -top-3 right-3 hover:shadow-md"
                >
                  <BookmarkSimple
                    alignmentBaseline="hanging"
                    weight={bookmarks.includes(tool.id) ? "fill" : "bold"}
                    className={cn(
                      "px-2 pb-2 rounded-tl-none hover:shadow-none rounded-br-none",
                      bookmarks.includes(tool.id)
                        ? "text-primary"
                        : "text-muted-foreground"
                    )}
                  />
                </Button>
              ) : (
                <div className="relative float-right w-12 h-12 ">
                  <Lock
                    className="mt-4 rounded-tl-none rounded-br-none rounded-tr-md"
                    weight="bold"
                    size="24"
                  />
                </div>
              )}
              <div className="px-5 py-5">
                <div className="mb-16 rounded-2xl bg-secondary aspect-square max-w-14 max-h-14">
                  {/* @ts-expect-error bad typing */}
                  {React.createElement(icon, {
                    className: "w-full h-full p-3",
                  })}
                </div>
                <div className="self-end">
                  <h1 className="text-card-content">{tool.title}</h1>
                  <p className="text-small-content text-card-foreground text-[0.938rem] mt-[0.625rem]">
                    {tool.description}
                  </p>
                </div>
              </div>
            </Link>
          )}
        </div>
      </TooltipTrigger>
    </Tooltip>
  );
}
